import { DEFAULT_IMAGE_FORMAT, DEFAULT_SCALE } from './index'
import { checkAbortSignal, runTaskQueue } from './utils'
import { PDFParser, PDFPageRenderer, PDFEventEmitter, ZipProgressor } from './tools'

// PDF转化图片类
export class PDFConverter extends PDFEventEmitter {
  constructor() {
    super()
    this.abortController = null
    this.filename = ''
    this.parser = new PDFParser()
    this.ZipProgressor = new ZipProgressor()
    this.PDFPageRenderer = new PDFPageRenderer({ scale: DEFAULT_SCALE })
    this.imageDataMap = new Map() // 使用Map存储分页图片数据
  }

  setFilename(filename) {
    this.filename = filename || ''
  }

  async getPDF(file) {
    // 单次进可解析单个文件
    if (this.abortController) {
      this.abortController.abort()
    }
    this.abortController = new AbortController()
    if (!file) throw new Error('[PDFConverter: getPdf]请选择文件')
    this.setFilename((file.name || '').split('.')[0])
    return this.parser.parse(file, this.abortController.signal)
  }
  /**
   * 异步获取图像并将其打包为ZIP文件（可选）
   * @param {Object} [options] - 配置选项
   * @param {Object} [options.pdf] - 要处理的PDF文档对象
   * @param {Function} [options.callback] - 任务完成后的回调函数
   * @param {AbortController} [options.abortController=this.abortController] - 用于中止任务的中断控制器
   * @param {boolean} [options.zip=false] - 是否生成ZIP压缩包的标志
   * @returns {void}
   */
  async getImages({ pdf, callback, abortController = this.abortController, zip = false } = {}) {
    this.ZipProgressor.init(this.filename)
    this.clearImageList()
    const taskList = this._createTasks({ pdf, zip }, abortController)
    runTaskQueue(taskList, async () => {
      try {
        if (typeof callback === 'function') {
          await callback()
        }
      } finally {
        if (zip) {
          this.ZipProgressor.generateAsync({ clear: true })
        }
        this.emit('complete')
      }
    }, abortController ? abortController.signal : null)
  }

  _createTasks({ pdf, zip = false } = {}, { signal } = this.abortController || {}) {
    const tasks = []
    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
      tasks.push(async () => {
        checkAbortSignal(signal, '图片转化任务取消')
        this.emit('pageStart', pageNum)
        try {
          await this._processPage({ pdf, pageNum, zip, signal })
          console.log(`页面${pageNum}转化完成`)
        } catch (error) {
          console.error(`第${pageNum}页处理失败:`, error)
        } finally {
          this.emit('pageComplete', pageNum)
        }
      })
    }
    return tasks
  }

  async _processPage({
    pdf,
    pageNum,
    zip = false,
    signal = this.abortController ? this.abortController.signal : null
  } = {}) {
    checkAbortSignal(signal, 'PDF parsing aborted')
    const page = await pdf.getPage(pageNum)
    checkAbortSignal(signal, 'PDF parsing aborted')
    try {
      const blob = await this.getImageDataFromPage({ page, signal })
      checkAbortSignal(signal, 'PDF parsing aborted')
      this.addImageCache(pageNum, blob)
      if (zip && this.ZipProgressor) {
        await this.ZipProgressor.addFile(`${this.filename}-${pageNum}.${DEFAULT_IMAGE_FORMAT}`, blob, { binary: true })
      }
    } finally {
      page.cleanup()
    }
  }

  async getImageDataFromPage({
    page,
    signal = this.abortController ? this.abortController.signal : null
  } = {}) {
    const canvas = this.PDFPageRenderer.prepareCanvas(page)
    checkAbortSignal(signal, 'PDF parsing aborted')
    await this.PDFPageRenderer.render({ page, canvas })
    checkAbortSignal(signal, 'PDF parsing aborted')
    return await this.PDFPageRenderer.getCanvasImage({ canvas, signal })
  }

  addImageCache(pageNum, imageData) {
    this.imageDataMap.set(pageNum, imageData)
  }
  // 获取图片列表
  getImageList(pageNum = null) {
    return pageNum ? this.imageDataMap.get(pageNum) : [...this.imageDataMap.values()]
  }

  // 清理图片列表
  clearImageList() {
    this.imageDataMap.clear()
  }

  // 设置图片精度
  setImageQuality(quality = this.PDFPageRenderer.quality) {
    this.PDFPageRenderer.setQuality(quality)
  }
  // 设置图片格式
  setImageFormat(format = this.PDFPageRenderer.format) {
    this.PDFPageRenderer.setFormat(format)
  }

  // 设置图片缩放比例
  setImageScale(scale = this.PDFPageRenderer.scale) {
    this.PDFPageRenderer.setScale(scale)
  }

  /**
   * 初始化
   * @memberof PDFConverter
   */
  init() {
    if (!this.parser) return
    this.parser.init()
  }
  /**
   * 中断任务
   * @memberof PDFConverter
   */
  abort() {
    try {
      if (!(this.abortController instanceof AbortController)) return
      this.abortController.abort()
      this.abortController = new AbortController()
    } finally {
      if (this.ZipProgressor) {
        this.ZipProgressor.clear()
      }
      this.clearImageList()
    }
  }

  /**
   * 中断PDF解析
   * @param {*} pdf
   * @param {*} [{ clearWorker = false, removeListeners = true }={}]
   * @memberof PDFConverter
   */
  terminate(pdf, { clearWorker = false, removeListeners = true } = {}) {
    if (pdf) {
      try {
        pdf.destroy()
      } catch (err) {
        console.error('PDF文档销毁失败:', err)
      }
    }
    this.abort()
    if (clearWorker) {
      // 释放全局资源
      this.parser.clear()
    }
    if (removeListeners) {
      // 清理listener
      this.clearListener()
    }
    this.setFilename('')
  }
}

const PDFPorgressorInstance = new PDFConverter()
export default PDFPorgressorInstance
